import { observeUndefinedFields } from '@/lib/firestoreUtils';
import { message } from '@/lib/notify/apNotify';
import { BaseRecord, CrudFilters, CrudSort, DataProvider } from '@refinedev/core';
import { get, off, onDisconnect, onValue, ref, set } from 'firebase/database';
import {
  collection,
  doc,
  getCountFromServer,
  getDoc,
  getDocs,
  increment,
  limit,
  onSnapshot,
  orderBy,
  query,
  QueryConstraint,
  serverTimestamp,
  startAfter,
  where,
  WhereFilterOp,
  writeBatch,
} from 'firebase/firestore';

type FirestoreDataProvider1 = Omit<
  DataProvider,
  'getApiUrl' | 'getOne' | 'create' | 'update' | 'deleteOne'
>;

export type RealtimeDbCreateManyValue = {
  resource: string;
  resourcePath?: string;
  uuids?: string[];
  ids?: string[];
  data: any;
  actionType?: 'set' | 'update' | 'delete';
  options?: {
    createNewDocId?: boolean;
    newDocId?: string;
  };
};

type FirestoreParamsWithMeta = {
  meta?: any;
};

/**
 * Applies a single write op (set/update/delete) to a Firestore batch.
 * Extracted so every code path that builds a batch routes the actionType
 * switch through one place.
 */
const writeToBatch = (
  batch: ReturnType<typeof writeBatch>,
  docRef: ReturnType<typeof doc>,
  data: any,
  actionType?: 'set' | 'update' | 'delete'
) => {
  if (actionType === 'update') batch.update(docRef, data, { merge: true });
  else if (actionType === 'delete') batch.delete(docRef);
  else batch.set(docRef, data, { merge: true });
};

/**
 * Builds and commits all writeBatches for a createMany call. Firestore is
 * initialized with `ignoreUndefinedProperties: true`, so undefined values are
 * silently stripped by the SDK at the wire level — we run observeUndefinedFields
 * on each payload to keep the data-integrity signal in Sentry without needing
 * a fallback / retry path.
 *
 * Honors Firestore's 500-op-per-batch limit by rolling a fresh batch as needed.
 * Returns the array of batch commit results so the caller can pass them to
 * meta.onSuccess. Throws on commit failure — the caller surfaces the error.
 */
const buildAndCommitCreateManyBatches = async (
  firestoreDB: any,
  values: RealtimeDbCreateManyValue[],
  meta: any
): Promise<any[]> => {
  const BATCH_SIZE = 500; // Firestore batch limit

  // Process in chunks if needed
  let currentBatch = writeBatch(firestoreDB);
  let operationsInCurrentBatch = 0;
  const batches: any[] = [];

  for (let i = 0; i < values.length; i++) {
    const transform = meta?.transformRealtimeDBProps;
    const value = transform ? transform(values[i]) : values[i];

    try {
      if (value.ids) {
        // then update all objects with values
        for (const id of value.ids) {
          // Check if we need to start a new batch
          if (operationsInCurrentBatch >= BATCH_SIZE) {
            batches.push(currentBatch);
            currentBatch = writeBatch(firestoreDB);
            operationsInCurrentBatch = 0;
          }

          const docPath = `${value.resource}/${id}`;
          try {
            const docRef = doc(firestoreDB, docPath);
            if (value.data) {
              // Observability is best-effort: a crash inside the walk (e.g. a
              // Firestore sentinel that JSON.stringify can't serialize, or a
              // circular reference like DocumentReference) MUST NOT abort the
              // write. Log + Sentry-capture if it throws, then continue to the
              // actual write. Real fix for the announcement-not-arriving bug:
              // before, this throw was bubbling up and silently killing the
              // entire batch (fire-and-forget callers see no error).
              try {
                observeUndefinedFields(value.data, docPath);
              } catch (obsError) {
                console.warn(
                  `observeUndefinedFields threw for ${docPath} — continuing with write:`,
                  obsError
                );
              }
              writeToBatch(currentBatch, docRef, value.data, value.actionType);
              operationsInCurrentBatch++;
            }
          } catch (docError) {
            console.error(`createMany error processing document ${docPath}:`, docError);
            throw docError;
          }
        }
      } else {
        // Check if we need to start a new batch
        if (operationsInCurrentBatch >= BATCH_SIZE) {
          batches.push(currentBatch);
          currentBatch = writeBatch(firestoreDB);
          operationsInCurrentBatch = 0;
        }

        // update all records with values
        let docRef: any;
        if (value.options?.createNewDocId) {
          const collectionRef = collection(firestoreDB, value.resource);
          docRef = doc(collectionRef);
        } else {
          docRef = doc(firestoreDB, value.resource);
        }
        const docData = value.data;
        if (docData) {
          const docDataWithTimestamp = {
            ...docData,
            updatedAt: serverTimestamp(),
          };
          // Observability is best-effort — see comment on the parallel call
          // above. Must never abort the actual write, even on hostile input.
          try {
            observeUndefinedFields(docDataWithTimestamp, value.resource);
          } catch (obsError) {
            console.warn(
              `observeUndefinedFields threw for ${value.resource} — continuing with write:`,
              obsError
            );
          }
          writeToBatch(currentBatch, docRef, docDataWithTimestamp, value.actionType);
          operationsInCurrentBatch++;
        }
      }
    } catch (valueError) {
      console.error(`createMany error processing value ${i + 1}/${values.length}:`, valueError);
      throw valueError;
    }
  }

  // Add the last batch if it has operations
  if (operationsInCurrentBatch > 0) {
    batches.push(currentBatch);
  }

  // Commit all batches
  const batchResults: any[] = [];
  for (let i = 0; i < batches.length; i++) {
    const batchResult = await batches[i].commit();
    batchResults.push(batchResult);
  }

  return batchResults;
};

type FirestoreDataProvider = {
  createMany: (params: { values: RealtimeDbCreateManyValue[]; meta: any }) => Promise<any>;
  // updateMany: (params: FirestoreUpdateManyParamsByIds) => Promise<any>;
  getList: (params: {
    resource: string;
    pagination: any;
    sorters: any;
    filters: any;
    meta: any;
  }) => Promise<any>;
  getOne: (params: { resource: string; id: string; meta: any }) => Promise<any>;
  setPresenceData: (params: { resource: string; meta: any }) => Promise<any>;
  getPresenceData: (params: { resource: string; meta: any }) => Promise<any>;
  watchFirebaseUserOnline: (params: { meta: any }) => Promise<any>;
  incrementValue: (params: { previous?: number }) => ReturnType<typeof increment>;
};

const buildFirestoreQueryConstraints = (
  filters?: CrudFilters,
  sorters?: CrudSort[]
): QueryConstraint[] => {
  const constraints: QueryConstraint[] = [];
  if (filters) {
    filters.forEach((filter) => {
      if ('field' in filter) {
        let operator: WhereFilterOp;
        switch (filter.operator) {
          case 'eq':
            operator = '==';
            break;
          case 'ne':
            operator = '!=';
            break;
          case 'lt':
            operator = '<';
            break;
          case 'gt':
            operator = '>';
            break;
          case 'lte':
            operator = '<=';
            break;
          case 'gte':
            operator = '>=';
            break;
          case 'in':
            operator = 'in';
            break;
          case 'nin':
            operator = 'not-in';
            break;
          case 'contains':
            operator = 'array-contains';
            break;
          default:
            console.warn(
              `[FirestoreProvider] Unsupported Firestore filter operator: ${filter.operator}`
            );
            return;
        }
        constraints.push(where(filter.field, operator, filter.value));
      } else {
        console.warn(
          '[FirestoreProvider] Logical OR filters are not fully supported by this basic builder. Filters will be ANDed.'
        );
        if (filter.operator === 'and' && Array.isArray(filter.value)) {
          filter.value.forEach((f) => {
            // Basic nested AND support
            if ('field' in f && f.operator === 'eq')
              constraints.push(where(f.field, '==', f.value));
            // Extend with other operators if needed for nested AND
          });
        }
      }
    });
  }
  if (sorters) {
    sorters.forEach((sorter) => {
      constraints.push(orderBy(sorter.field, sorter.order));
    });
  }
  return constraints;
};

export const firestoreDataProvider: any = (firebaseServicesProps?: any): FirestoreDataProvider => {
  const initializedFirebaseServices = firebaseServicesProps;
  // if (!initializedFirebaseServices) {
  //   initializedFirebaseServices = initializeFirebaseApp();
  //   if (!initializedFirebaseServices) {
  //     throw new Error('Firebase services are not initialized');
  //   }
  // }
  const firestoreDB = initializedFirebaseServices?.firestoreDB;

  return {
    watchFirebaseUserOnline: async ({ meta }) => {
      const firebaseRealtimeDb = initializedFirebaseServices.firebaseRealtimeDb;
      const connectedRef = ref(firebaseRealtimeDb, '.info/connected');
      const unsubscribe = onValue(connectedRef, (snapshot) => {
        if (meta?.onRealtimeEvent) {
          meta.onRealtimeEvent('ONLINE_STATUS', snapshot.val());
        }
        // Listen for value changes
        // setIsConnected(snapshot.val()); // Update the state based on the value
      });
      return {
        meta: {
          unsubscribe,
        },
      };
    },
    // Firebase increment is server-side atomic and doesn't accept a previous value.
    // The previous parameter is kept in the signature for API consistency with Supabase provider,
    // but is intentionally ignored. Firebase will atomically increment by 1 on the server.
    incrementValue: () => {
      return increment(1);
    },
    getPresenceData: async ({ resource, meta }) => {
      console.log('getPresenceData', resource, meta);
      const firebaseRealtimeDb = initializedFirebaseServices.firebaseRealtimeDb;
      const presenceRef = ref(firebaseRealtimeDb, resource);
      const presenceSnapshot = await get(presenceRef);
      const presenceData = presenceSnapshot.val();
      console.log('presenceData check line 163 check  =====>', presenceData);
      if (meta?.onRealtimeEvent) {
        // onChildAdded(presenceRef, (snapshot) => {
        //   const presenceData = snapshot.val();
        //   meta?.onRealtimeEvent('ADDED', presenceData);
        //   console.log('presenceData', presenceData);
        // });
        // onChildRemoved(presenceRef, (snapshot) => {
        //   const presenceData = snapshot.val();
        //   meta?.onRealtimeEvent('REMOVED', presenceData);
        //   console.log('presenceData', presenceData);
        // });
        // onChildChanged(presenceRef, (snapshot) => {
        //   const presenceData = snapshot.val();
        //   meta?.onRealtimeEvent('UPDATED', presenceData);
        //   console.log('presenceData', presenceData);
        // });
        onValue(presenceRef, (snapshot) => {
          const presenceData = snapshot.val();
          meta?.onRealtimeEvent('PRESENCE_DATA', presenceData);
        });
      }
      const unsubscribe = () => {
        off(presenceRef);
      };

      return {
        data: presenceData,
        meta: {
          unsubscribe,
        },
      };
    },
    // setPresenceData: async ({ resource, onlineStatus = 'online', meta }) => {
    //   console.log('setPresenceData', resource, meta);
    //   const firebaseRealtimeDb = initializedFirebaseServices.firebaseRealtimeDb;

    //   // Reference to the user's specific presence node
    //   const presenceRef = ref(firebaseRealtimeDb, resource);

    //   // Reference to Firebase's special connection state node
    //   const connectedRef = ref(firebaseRealtimeDb, '.info/connected');

    //   // Define presence payloads using serverTimestamp for accuracy
    //   const disconnectPayload = {
    //     state: 'offline',
    //     last_changed: serverTimestamp(),
    //   };

    //   const payload = {
    //     state: onlineStatus,
    //     last_changed: serverTimestamp(),
    //   };

    //   // Set up the listener for connection state changes
    //   const unsubscribeFromConnection = onValue(connectedRef, (snapshot) => {
    //     message.error('setPresenceData unsubscribeFromConnection ====>', snapshot.val());
    //     // This callback fires whenever the connection state changes
    //     console.log('setPresenceData snapshot ====>', snapshot.val());
    //     if (snapshot.val() === false) {
    //       // The onDisconnect handler (set below) will take care of updating the status to offline.
    //       // No action is needed here.
    //       return;
    //     }

    //     // When the user connects or reconnects:
    //     // 1. Set the onDisconnect handler. This is a promise that resolves when the write is confirmed by the server.
    //     onDisconnect(presenceRef)
    //       .set(disconnectPayload)
    //       .then(() => {
    //         message.error('setPresenceData onDisconnect ====>', snapshot.val());
    //         // 2. Once the onDisconnect is established, set the user's state to online.
    //         // This ensures that the user isn't marked as online before the offline handler is ready.
    //         set(presenceRef, payload);
    //       });
    //   });

    //   // The main unsubscribe function returned by the liveProvider
    //   const unsubscribe = () => {
    //     // Stop listening to connection state changes
    //     unsubscribeFromConnection();
    //     message.error('setPresenceData unsubscribe ====>');
    //     // When the component unmounts or user navigates away, manually set the status to offline.
    //     // This provides a faster, more responsive offline status for UI purposes.
    //     set(presenceRef, disconnectPayload);
    //   };

    //   return {
    //     data: null,
    //     meta: {
    //       unsubscribe,
    //     },
    //   };
    // },
    setPresenceData: async ({ resource }) => {
      const firebaseRealtimeDb = initializedFirebaseServices.firebaseRealtimeDb;
      const presenceRef = ref(firebaseRealtimeDb, resource);
      const connectedRef = ref(firebaseRealtimeDb, '.info/connected');

      const offlinePayload = {
        state: 'offline',
        last_changed: serverTimestamp(),
      };

      const onlinePayload = {
        state: 'online',
        last_changed: serverTimestamp(),
      };

      const unsubscribeFromPresence = onValue(presenceRef, (snap) => {
        const currentVal = snap.val() !== null ? snap.val() : { state: 'offline' };
        // We are setting the presence to online when the user comes online and its previous value was offline
        if (currentVal.state === 'offline') {
          set(presenceRef, onlinePayload);
        }
      });

      const unsubscribeFromConnection = onValue(connectedRef, (snapshot) => {
        if (snapshot.val() === true) {
          // Firebase will automatically set the presence to offline when the user disconnects
          onDisconnect(presenceRef)
            .set(offlinePayload)
            .then(() => {
              // We are trying to set the presence to online whenever the user comes online
              // but its not always working as there's delay in onDisconnect and info/connected data,
              // so we are setting the presence to online when the user comes online inside the presenceRef onValue listener
              set(presenceRef, onlinePayload);
            });
        }
      });

      const unsubscribe = () => {
        unsubscribeFromPresence();
        unsubscribeFromConnection();
        set(presenceRef, offlinePayload);
      };

      return {
        data: null,
        meta: {
          unsubscribe,
        },
      };
    },
    getOne: async ({ resource, id, meta }) => {
      console.log('getOne 201', resource, id, meta);
      const docPath = `${resource}/${id}`;
      console.log('getOne docPath', docPath);
      const docRef = doc(firestoreDB, docPath);
      const docSnapshot = await getDoc(docRef);
      const docData = docSnapshot.data();
      const docDataWithId = { ...docData, id: docSnapshot.id };
      let unsubscribe: (() => void) | undefined = undefined;
      if (meta?.onRealtimeEvent) {
        console.log('getOne meta?.onRealtimeEvent', meta?.onRealtimeEvent);
        const localUnsubscribe = onSnapshot(docRef, (docSnapshot) => {
          console.log('getOne docSnapshot', docSnapshot);
          if (docSnapshot.exists()) {
            const docData = docSnapshot.data();
            const docDataWithId = { ...docData, id: docSnapshot.id };
            meta.onRealtimeEvent('UPDATED', docDataWithId);
          } else {
            // meta.onRealtimeEvent('REMOVED', { id });
          }
        });
        unsubscribe = () => {
          localUnsubscribe();
        };
      }
      return {
        data: docDataWithId,
        meta: {
          unsubscribe,
        },
      };
    },
    createMany: async ({ values, meta }) => {
      const firebaseAuthUser = initializedFirebaseServices.firebaseAuth.currentUser;
      console.log(
        initializedFirebaseServices,
        'createMany firebaseAuthUser line no. 280 ==>',
        firebaseAuthUser
      );

      try {
        // Firestore SDK is initialized with `ignoreUndefinedProperties: true`,
        // so undefined values are silently stripped at the wire level.
        // observeUndefinedFields reports any undefineds to Sentry per-payload
        // inside the builder, so we don't need a retry / sanitize fallback here.
        const batchResults = await buildAndCommitCreateManyBatches(firestoreDB, values, meta);
        if (meta?.onSuccess) {
          console.log('createMany meta.onSuccess', batchResults);
          meta.onSuccess(batchResults);
        }
        return {
          success: true,
        };
      } catch (error: any) {
        if (meta?.onError) {
          meta.onError(error);
        }
        if (
          import.meta.env.VITE_MODE === 'development' ||
          import.meta.env.VITE_MODE === 'staging'
        ) {
          message.error('Create Many Error');
        }
        console.error('createMany error:', error);
        throw error;
      }
    },
    // updateMany: async ({ resource, variables, ids, meta }) => {
    //   console.log('updateMany', meta);
    //   const batch = writeBatch(firestoreDB);
    //   const updatedData: any[] = [];
    //   if (ids) {
    //     // then update all objects with values
    //     for (const id of ids) {
    //       const docPath = `${resource}/${id}`;
    //       const docRef = doc(firestoreDB, docPath);
    //       // const docData = values;
    //       if (variables) {
    //         batch.update(docRef, variables, { merge: true });
    //         // updatedData.push({ ...docData, id });
    //       }
    //     }
    //   } else {
    //     // update all records with values
    //   }
    //   const batchResult = await batch.commit();
    //   console.log('updateMany batchResult', batchResult);
    //   return {
    //     success: true,
    //     // data: updatedData,
    //   };
    // },

    getList: async ({ resource, pagination, sorters, filters, meta }) => {
      console.log('getList', resource, pagination, sorters, filters, meta);
      const startAfterDoc = meta?.lastVisibleDoc;
      const collRef = collection(firestoreDB, resource);
      const baseQueryConstraints = buildFirestoreQueryConstraints(filters, sorters);
      const paginatedQueryConstraints: QueryConstraint[] = [...baseQueryConstraints];

      // const paginatedQueryConstraints =
      // const paginatedQueryConstraints: QueryConstraint[] = [];
      if (pagination) {
        paginatedQueryConstraints.push(limit(pagination.pageSize));
        if (pagination.current > 1 && startAfterDoc) {
          paginatedQueryConstraints.push(startAfter(startAfterDoc));
        } else if (pagination.current > 1 && !startAfterDoc) {
          console.warn(
            `[FirestoreProvider] Attempting to fetch page ${pagination.current} for ${resource} without a 'startAfter' document.`
          );
        }
      }
      // const initialQuery = query(collRef, ...paginatedQueryConstraints);

      const initialQuery = query(collRef, ...paginatedQueryConstraints);
      const querySnapshot = await getDocs(initialQuery);
      const initialData = querySnapshot.docs.map((doc) => {
        const docData = doc.data();
        console.log('data firestore new - datum', docData);
        // Object.keys(docData).forEach((key) => {
        //   console.log('data firestore new - key', key, docData[key]);
        //   if (docData[key] instanceof Timestamp) {
        //     console.log('data firestore new - timestamp', docData[key]);
        //     docData[key] = (docData[key] as Timestamp).toDate();
        //   }
        // });
        return { ...docData, id: doc.id } as BaseRecord;
      });
      const lastVisibleDoc = querySnapshot.docs[querySnapshot.docs.length - 1];
      // const docs = querySnapshot.docs.map((doc) => doc.data());
      // APL-11850: the aggregation count is a second serial server round-trip on
      // every list read — opt-in via meta.needsTotal (no consumer needs it today).
      let total: number | undefined = undefined;
      if (meta?.needsTotal) {
        const queryWithoutPagination = query(collRef, ...baseQueryConstraints);
        try {
          const snapshot = await getCountFromServer(queryWithoutPagination);
          total = snapshot.data().count;
        } catch (countError) {
          console.warn(
            `[FirestoreProvider] Could not fetch total count for ${resource}.`,
            countError
          );
        }
      }

      let unsubscribeFn: (() => void) | undefined = undefined;
      if (meta?.realtime && meta.onRealtimeEvent) {
        // unsubscribeFn = onSnapshot(collRef, (snapshot) => {
        //   const docs = snapshot.docs.map((doc) => doc.data());
        //   meta.onRealtimeEvent(docs);
        // });
        const localUnsubscribe = onSnapshot(initialQuery, (snapshot) => {
          snapshot.docChanges().forEach((change) => {
            const doc = change.doc.data();
            const docWithId = { ...doc, id: change.doc.id };
            if (change.type === 'added') {
              // meta.onRealtimeEvent('ADDED', doc);
              meta.onRealtimeEvent('ADDED', docWithId);
            } else if (change.type === 'modified') {
              meta.onRealtimeEvent('UPDATED', docWithId);
            } else if (change.type === 'removed') {
              meta.onRealtimeEvent('REMOVED', docWithId);
            }
          });
        });

        unsubscribeFn = () => {
          // console.log(`[FirestoreProvider] Unsubscribing from REALTIME listener for ${resource}.`);
          localUnsubscribe();
        };
      }

      // const total = await getCountFromServer(query);
      // console.log('data firestore new - initialData', initialData);
      const finalResponse = {
        data: initialData,
        total: total,
        meta: {
          lastVisibleDoc,
          unsubscribeFn,
        },
      };
      // if (unsubscribeFn) {
      //   finalResponse.meta.unsubscribeFn = unsubscribeFn;
      // }
      return finalResponse;
    },
  };
};

// // firestoreProvider.ts
// import {
//   BaseRecord,
//   CrudFilters,
//   CrudOperators,
//   CrudSort,
//   DataProvider,
//   MetaQuery,
//   Pagination,
// } from '@refinedev/core';
// import {
//   addDoc,
//   collection,
//   deleteDoc,
//   doc,
//   documentId,
//   getCountFromServer,
//   getDoc,
//   getDocs,
//   limit,
//   orderBy,
//   query,
//   serverTimestamp,
//   startAfter,
//   Timestamp,
//   updateDoc,
//   where,
//   writeBatch,
// } from 'firebase/firestore';
// import { firebaseServices } from '../liveProvider';

// // // Firebase config - these will be provided globally in the Canvas environment
// // declare const __firebase_config: string;
// // declare const __app_id: string;
// // declare const __initial_auth_token: string;

// // // Helper to get user and app ID
// // const getAppId = (): string => (typeof __app_id !== 'undefined' ? __app_id : 'default-app-id');

// // const getFirebaseConfig = () => {
// //   if (typeof __firebase_config === 'undefined') {
// //     console.warn(
// //       'Firebase config (__firebase_config) is not defined. Using placeholder. Ensure this is properly configured in your environment.'
// //     );
// //     // Placeholder for local development if globals are not set
// //     return {
// //       apiKey: 'YOUR_API_KEY', // Replace with your actual config for local dev
// //       authDomain: 'YOUR_AUTH_DOMAIN',
// //       projectId: 'YOUR_PROJECT_ID', // e.g., "my-refine-app"
// //       storageBucket: 'YOUR_STORAGE_BUCKET',
// //       messagingSenderId: 'YOUR_MESSAGING_SENDER_ID',
// //       appId: 'YOUR_APP_ID',
// //     };
// //   }
// //   try {
// //     return JSON.parse(__firebase_config);
// //   } catch (e) {
// //     console.error("Failed to parse __firebase_config. Ensure it's a valid JSON string.", e);
// //     throw new Error('Invalid Firebase configuration provided.');
// //   }
// // };

// // let firebaseApp: FirebaseApp;
// // let db: ReturnType<typeof getFirestore>;
// // let auth: Auth;
// // let currentUserId: string | null = null;
// // let isAuthInitialized = false;

// // // Initialize Firebase
// // try {
// //   const firebaseConfig = getFirebaseConfig();
// //   firebaseApp = initializeApp(firebaseConfig);

// //   // Example of initializing Firestore with settings (optional)
// //   // db = initializeFirestore(firebaseApp, {
// //   //   cacheSizeBytes: CACHE_SIZE_UNLIMITED, // Example: unlimited cache
// //   //   ignoreUndefinedProperties: true, // Good for preventing errors with undefined fields
// //   // });
// //   db = getFirestore(firebaseApp);
// //   auth = getAuth(firebaseApp);

// //   // Enable Firestore persistence (optional, but recommended for offline capabilities)
// //   enableIndexedDbPersistence(db).catch((err) => {
// //     if (err.code === 'failed-precondition') {
// //       console.warn(
// //         'Firestore persistence failed: Multiple tabs open or other precondition not met.'
// //       );
// //     } else if (err.code === 'unimplemented') {
// //       console.warn('Firestore persistence failed: Browser does not support persistence.');
// //     }
// //   });
// // } catch (error) {
// //   console.error('Firebase initialization failed:', error);
// //   // Handle initialization error, perhaps by disabling Firebase features or showing an error to the user
// // }

// // // Authentication logic
// // const authenticateFirebaseUser = async (): Promise<void> => {
// //   if (!auth) {
// //     console.error('Firebase Auth is not initialized.');
// //     currentUserId = crypto.randomUUID(); // Fallback
// //     isAuthInitialized = true;
// //     return;
// //   }
// //   try {
// //     if (typeof __initial_auth_token !== 'undefined' && __initial_auth_token) {
// //       await signInWithCustomToken(auth, __initial_auth_token);
// //     } else {
// //       // Attempt to sign in anonymously if no custom token
// //       // This ensures auth.currentUser is populated for rules or user-specific data
// //       if (!auth.currentUser) {
// //         await signInAnonymously(auth);
// //       }
// //     }
// //   } catch (error) {
// //     console.error('Error during Firebase authentication:', error);
// //     // Fallback to anonymous if custom token fails or is not present, and no user is signed in
// //     if (!auth.currentUser) {
// //       try {
// //         await signInAnonymously(auth);
// //       } catch (anonError) {
// //         console.error('Error during anonymous authentication fallback:', anonError);
// //       }
// //     }
// //   } finally {
// //     isAuthInitialized = true;
// //     currentUserId = auth.currentUser ? auth.currentUser.uid : crypto.randomUUID();
// //   }
// // };

// // // Listen to auth state changes to keep currentUserId updated
// // if (auth) {
// //   onAuthStateChanged(auth, (user) => {
// //     if (user) {
// //       currentUserId = user.uid;
// //     } else {
// //       // User is signed out or authentication failed.
// //       // Depending on app logic, might re-trigger anonymous sign-in or use a guest ID.
// //       currentUserId = crypto.randomUUID();
// //     }
// //     if (!isAuthInitialized) {
// //       // Ensure this runs at least once
// //       isAuthInitialized = true;
// //     }
// //     // console.log("Firebase Auth state changed. User ID:", currentUserId);
// //   });
// // }

// // // Ensure auth is initialized before operations
// // const ensureAuthReady = async (): Promise<void> => {
// //   if (isAuthInitialized && currentUserId) return; // Already ready

// //   // If auth object exists but not initialized fully, run authentication
// //   if (auth && !isAuthInitialized) {
// //     await authenticateFirebaseUser();
// //   } else if (!auth) {
// //     // Critical failure if auth is not available
// //     console.error('Firebase Auth is not available. Cannot ensure auth readiness.');
// //     isAuthInitialized = true; // Mark as "checked" to avoid infinite loops
// //     currentUserId = crypto.randomUUID(); // Fallback ID
// //     return;
// //   }

// //   // Fallback if currentUserId is still not set after initial attempts
// //   if (!currentUserId) {
// //     currentUserId = auth?.currentUser?.uid || crypto.randomUUID();
// //   }
// // };

// const ensureAuthReady = async () => {
//   // TODO: Implement auth check

//   return Promise.resolve(true);
// };

// // Firestore's WhereFilterOp type
// type FirebaseWhereFilterOp =
//   | '<'
//   | '<='
//   | '=='
//   | '!='
//   | '>='
//   | '>'
//   | 'array-contains'
//   | 'in'
//   | 'not-in'
//   | 'array-contains-any';

// // Helper to map Refine CrudOperators to Firestore WhereFilterOp
// const mapOperator = (operator: CrudOperators): FirebaseWhereFilterOp => {
//   // Ensure operators are of supported types by refine/core
//   const opMap: Partial<Record<CrudOperators, FirebaseWhereFilterOp>> = {
//     eq: '==',
//     ne: '!=',
//     lt: '<',
//     gt: '>',
//     lte: '<=',
//     gte: '>=',
//     in: 'in',
//     nin: 'not-in',
//     contains: 'array-contains', // Primarily for array field contains
//     // 'startswith': // Firestore doesn't support startswith directly. Requires >= and < trick.
//   };
//   if (operator in opMap) {
//     return opMap[operator] as FirebaseWhereFilterOp;
//   }
//   console.warn(`Unsupported operator: ${operator}, defaulting to '=='`);
//   return '==';
// };

// // Helper to convert Firestore Timestamps in data objects to ISO strings
// const convertTimestampsToISO = (data: any): any => {
//   if (!data) return data;
//   if (Array.isArray(data)) {
//     return data.map(convertTimestampsToISO);
//   }
//   if (data instanceof Timestamp) {
//     return data.toDate().toISOString();
//   }
//   if (typeof data === 'object' && data !== null) {
//     const newData: { [key: string]: any } = {};
//     for (const key in data) {
//       newData[key] = convertTimestampsToISO(data[key]);
//     }
//     return newData;
//   }
//   return data;
// };

// export interface FirestoreProviderOptions {
//   isDataPublic?: boolean;
//   /** * A function to dynamically determine the collection path.
//    * @param resource The name of the resource (e.g., "posts").
//    * @param userId The current user's ID (can be null if not authenticated or data is public).
//    * @param appId The application ID.
//    * @returns The full Firestore collection path string.
//    */
//   getCollectionPathFn?: (resource: string, userId: string | null, appId: string) => string;
// }

// // Main Firestore Data Provider
// export const firestoreProvider = (options: FirestoreProviderOptions = {}): DataProvider => {
//   const { isDataPublic = true, getCollectionPathFn } = options;
//   const { firestoreDB } = firebaseServices;

//   // Default path generation logic
//   const getDefaultPath = (resource: string) => {
//     const appId = getAppId();
//     if (getCollectionPathFn) {
//       return getCollectionPathFn(resource, currentUserId, appId);
//     }
//     if (isDataPublic || !currentUserId) {
//       // Fallback to public if user not available for private path
//       if (!isDataPublic && !currentUserId) {
//         console.warn(
//           `Requested private path for '${resource}' but no user ID available. Defaulting to public path.`
//         );
//       }
//       return `artifacts/${appId}/public/data/${resource}`;
//     }
//     // Private path
//     return `artifacts/${appId}/users/${currentUserId}/${resource}`;
//   };

//   return {
//     getApiUrl: () => {
//       if (!firebaseApp || !firebaseApp.options.projectId) {
//         return 'firestore://unknown-project';
//       }
//       return `firestore://${firebaseApp.options.projectId}`;
//     },

//     getList: async <TData extends BaseRecord = BaseRecord>({
//       resource,
//       pagination,
//       sorters,
//       filters,
//       meta,
//     }: {
//       resource: string;
//       pagination?: Pagination;
//       sorters?: CrudSort[];
//       filters?: CrudFilters;
//       meta?: MetaQuery;
//     }) => {
//       if (!db) throw new Error('Firestore is not initialized.');
//       await ensureAuthReady();
//       const collPath = meta?.collectionPath || getDefaultPath(resource);
//       const collRef = collection(db, collPath);

//       let firestoreQuery = query(collRef);

//       // Apply filters
//       if (filters && filters.length > 0) {
//         filters.forEach((filter) => {
//           if ('field' in filter) {
//             // ConditionalFilter
//             const { field, operator, value } = filter;
//             if (
//               operator === 'contains' &&
//               typeof value === 'string' &&
//               !meta?.isTimestampField?.(field)
//             ) {
//               console.warn(
//                 `Firestore 'array-contains' is typically for array fields. For string 'contains' on field '${field}', consider a search service or different data modeling. This filter might not work as expected for simple string containment.`
//               );
//               // If you intend to use it for prefix search (e.g. name starts with "Jo")
//               // firestoreQuery = query(firestoreQuery, where(field, '>=', value), where(field, '<=', value + '\uf8ff'));
//               // For now, applying as array-contains or equality based on mapOperator
//               firestoreQuery = query(firestoreQuery, where(field, mapOperator(operator), value));
//             } else if (
//               [
//                 'ncontains',
//                 'containss',
//                 'ncontainss',
//                 'null',
//                 'nnull',
//                 'startswith',
//                 'endswith',
//               ].includes(operator)
//             ) {
//               console.warn(
//                 `Operator '${operator}' for field '${field}' is not fully supported by this basic Firestore provider or requires specific Firestore indexing/query patterns. Filter may be ignored or behave unexpectedly.`
//               );
//             } else {
//               firestoreQuery = query(firestoreQuery, where(field, mapOperator(operator), value));
//             }
//           } else {
//             // LogicalFilter (and/or)
//             console.warn(
//               'Logical operators (AND/OR) in filters are not fully supported for complex nested structures by this provider. Multiple top-level filters are treated as AND. For OR queries, Firestore requires separate queries or its native OR query (limited).'
//             );
//           }
//         });
//       }

//       // Get total count matching filters
//       const countQuery = firestoreQuery; // Query for count is the same as filtered query before pagination/sorting for count
//       let total = 0;
//       try {
//         const snapshot = await getCountFromServer(countQuery);
//         total = snapshot.data().count;
//       } catch (error) {
//         console.error(`Error getting count from server for resource ${resource}:`, error);
//         // Fallback or rethrow: For robustness, you might want a fallback, but it can be slow.
//         // const allDocsSnapshot = await getDocs(countQuery);
//         // total = allDocsSnapshot.size;
//         throw new Error(`Failed to get count for ${resource}: ${(error as Error).message}`);
//       }

//       // Apply sorters
//       if (sorters && sorters.length > 0) {
//         sorters.forEach((sorter) => {
//           firestoreQuery = query(firestoreQuery, orderBy(sorter.field, sorter.order));
//         });
//       } else {
//         // Default sort if pagination is used without explicit sorters, often needed for consistent startAfter behavior
//         // Firestore requires an orderBy clause when using cursors (startAfter/startAt) unless ordering by document ID.
//         // If no sorters are provided and pagination is used, you might need a default sort field.
//         // For example: firestoreQuery = query(firestoreQuery, orderBy(documentId()));
//         // This is context-dependent. For now, we rely on explicit sorters or Firestore's default (if any).
//       }

//       // Apply pagination
//       if (pagination) {
//         const { current = 1, pageSize = 10 } = pagination;

//         if (current > 1 && total > 0) {
//           // Only apply startAfter if not on first page and there's data
//           // Query to get the last document of the previous page to use as a cursor
//           // The query for the cursor should have the same filters and sorters
//           const cursorQuery = query(firestoreQuery, limit((current - 1) * pageSize));
//           const prevPageSnapshot = await getDocs(cursorQuery);

//           if (!prevPageSnapshot.empty) {
//             const lastVisible = prevPageSnapshot.docs[prevPageSnapshot.docs.length - 1];
//             firestoreQuery = query(firestoreQuery, startAfter(lastVisible));
//           } else if ((current - 1) * pageSize >= total) {
//             // Requested page is out of bounds (current page starts beyond total items)
//             return { data: [] as TData[], total };
//           }
//         }
//         firestoreQuery = query(firestoreQuery, limit(pageSize));
//       }

//       const querySnapshot = await getDocs(firestoreQuery);
//       const data = querySnapshot.docs.map(
//         (doc) => convertTimestampsToISO({ ...doc.data(), id: doc.id }) as TData
//       );

//       return { data, total };
//     },

//     getMany: async <TData extends BaseRecord = BaseRecord>({
//       resource,
//       ids,
//       meta,
//     }: {
//       resource: string;
//       ids: string[];
//       meta?: MetaQuery;
//     }) => {
//       if (!db) throw new Error('Firestore is not initialized.');
//       await ensureAuthReady();
//       const collPath = meta?.collectionPath || getDefaultPath(resource);
//       const data: TData[] = [];

//       if (!ids || ids.length === 0) {
//         return { data: [] };
//       }

//       // Firestore 'in' query is limited to 30 values for documentId() when used with where(documentId(), 'in', ...)
//       // For other fields, 'in' is limited to 10.
//       const idChunks: string[][] = [];
//       for (let i = 0; i < ids.length; i += 30) {
//         // Chunk by 30 for documentId()
//         idChunks.push(ids.slice(i, i + 30).map(String)); // Ensure IDs are strings
//       }

//       for (const chunk of idChunks) {
//         if (chunk.length > 0) {
//           const q = query(collection(db, collPath), where(documentId(), 'in', chunk));
//           const querySnapshot = await getDocs(q);
//           querySnapshot.forEach((doc) => {
//             if (doc.exists()) {
//               data.push(convertTimestampsToISO({ ...doc.data(), id: doc.id }) as TData);
//             }
//           });
//         }
//       }
//       return { data };
//     },

//     getOne: async <TData extends BaseRecord = BaseRecord>({
//       resource,
//       id,
//       meta,
//     }: {
//       resource: string;
//       id: string;
//       meta?: MetaQuery;
//     }) => {
//       if (!db) throw new Error('Firestore is not initialized.');
//       await ensureAuthReady();
//       const docPath = meta?.collectionPath || getDefaultPath(resource);
//       const docRef = doc(db, docPath, String(id)); // Ensure ID is string
//       const docSnap = await getDoc(docRef);

//       if (docSnap.exists()) {
//         return { data: convertTimestampsToISO({ ...docSnap.data(), id: docSnap.id }) as TData };
//       } else {
//         throw new Error(`Document with id ${id} not found in ${resource}`);
//       }
//     },

//     create: async <TData extends BaseRecord = BaseRecord, TVariables = {}>({
//       resource,
//       variables,
//       meta,
//     }: {
//       resource: string;
//       variables: TVariables;
//       meta?: MetaQuery;
//     }) => {
//       if (!db) throw new Error('Firestore is not initialized.');
//       await ensureAuthReady();
//       const collPath = meta?.collectionPath || getDefaultPath(resource);

//       const { id, ...docData } = variables as any; // Exclude id if present, Firestore auto-generates

//       const docVariables = {
//         ...docData,
//         createdAt: serverTimestamp(),
//         updatedAt: serverTimestamp(),
//       };
//       const docRef = await addDoc(collection(db, collPath), docVariables);
//       // Fetch the document to get server-generated timestamps and ensure data consistency
//       const newDocSnapshot = await getDoc(docRef);
//       if (!newDocSnapshot.exists()) {
//         throw new Error(`Failed to create document in ${resource}`);
//       }
//       return {
//         data: convertTimestampsToISO({ ...newDocSnapshot.data(), id: newDocSnapshot.id }) as TData,
//       };
//     },

//     update: async <TData extends BaseRecord = BaseRecord, TVariables = {}>({
//       resource,
//       id,
//       variables,
//       meta,
//     }: {
//       resource: string;
//       id: string;
//       variables: TVariables;
//       meta?: MetaQuery;
//     }) => {
//       if (!db) throw new Error('Firestore is not initialized.');
//       await ensureAuthReady();
//       const docPath = meta?.collectionPath || getDefaultPath(resource);
//       const docRef = doc(db, docPath, String(id));

//       const docVariables = {
//         ...(variables as any),
//         updatedAt: serverTimestamp(),
//       };
//       // Remove 'createdAt' from variables if it exists, as it should not be changed on update
//       if ('createdAt' in docVariables) {
//         delete docVariables.createdAt;
//       }

//       await updateDoc(docRef, docVariables);
//       // Fetch the document to get merged data with new server timestamp
//       const updatedDocSnap = await getDoc(docRef);
//       if (!updatedDocSnap.exists()) {
//         // Should not happen if update was successful
//         throw new Error(`Document with id ${id} not found in ${resource} after update.`);
//       }
//       return {
//         data: convertTimestampsToISO({ ...updatedDocSnap.data(), id: updatedDocSnap.id }) as TData,
//       };
//     },

//     deleteOne: async <TData extends BaseRecord = BaseRecord, TVariables = {}>({
//       resource,
//       id,
//       variables,
//       meta,
//     }: {
//       resource: string;
//       id: string;
//       variables?: TVariables;
//       meta?: MetaQuery;
//     }) => {
//       if (!db) throw new Error('Firestore is not initialized.');
//       await ensureAuthReady();
//       const docPath = meta?.collectionPath || getDefaultPath(resource);
//       const docRef = doc(db, docPath, String(id));

//       // Optionally, get the document before deleting if you need to return its full data
//       // const docSnap = await getDoc(docRef);
//       // const originalData = docSnap.exists() ? { ...docSnap.data(), id: docSnap.id } : { id };

//       await deleteDoc(docRef);
//       // Refine expects the deleted record (or at least its ID) back.
//       // Return what was passed in `variables` or just the ID.
//       return { data: convertTimestampsToISO({ ...(variables || {}), id }) as TData };
//     },

//     // --- Batch operations ---
//     createMany: async <TData extends BaseRecord = BaseRecord, TVariables = {}>({
//       resource,
//       variables,
//       meta,
//     }: {
//       resource: string;
//       variables: TVariables[];
//       meta?: MetaQuery;
//     }) => {
//       if (!db) throw new Error('Firestore is not initialized.');
//       await ensureAuthReady();
//       const collPath = meta?.collectionPath || getDefaultPath(resource);
//       const batch = writeBatch(db);
//       const createdDataPromises: Promise<TData>[] = [];

//       for (const itemVariables of variables) {
//         const { id, ...docData } = itemVariables as any;
//         const newDocRef = doc(collection(db, collPath)); // Auto-generate ID
//         const docPayload = {
//           ...docData,
//           createdAt: serverTimestamp(),
//           updatedAt: serverTimestamp(),
//         };
//         batch.set(newDocRef, docPayload);
//         // To return the created data with server timestamps, we'd need to read after commit.
//         // For simplicity here, we'll construct an optimistic response.
//         // A more robust solution might involve fetching after commit if exact server values are critical.
//         createdDataPromises.push(
//           Promise.resolve({
//             ...docData,
//             id: newDocRef.id,
//             createdAt: new Date().toISOString(),
//             updatedAt: new Date().toISOString(),
//           } as TData)
//         );
//       }
//       await batch.commit();
//       const data = await Promise.all(createdDataPromises);
//       return { data: convertTimestampsToISO(data) as TData[] }; // Convert any optimistic Date to ISO
//     },

//     updateMany: async <TData extends BaseRecord = BaseRecord, TVariables = {}>({
//       resource,
//       ids,
//       variables,
//       meta,
//     }: {
//       resource: string;
//       ids: string[];
//       variables: TVariables;
//       meta?: MetaQuery;
//     }) => {
//       if (!db) throw new Error('Firestore is not initialized.');
//       await ensureAuthReady();
//       const docPathBase = meta?.collectionPath || getDefaultPath(resource);
//       const batch = writeBatch(db);
//       const updatedData: Partial<TData>[] = [];

//       const { createdAt, ...updatePayload } = variables as any; // Exclude createdAt

//       for (const id of ids) {
//         const docRef = doc(db, docPathBase, String(id));
//         const docDataWithTimestamp = {
//           ...updatePayload,
//           updatedAt: serverTimestamp(),
//         };
//         batch.update(docRef, docDataWithTimestamp);
//         updatedData.push({ ...updatePayload, id } as Partial<TData>);
//       }
//       await batch.commit();
//       // Similar to createMany, returning optimistic data. Re-fetch if server values are essential.
//       return {
//         data: convertTimestampsToISO(
//           updatedData.map((d) => ({ ...d, updatedAt: new Date().toISOString() }))
//         ) as TData[],
//       };
//     },

//     deleteMany: async <TData extends BaseRecord = BaseRecord, TVariables = {}>({
//       resource,
//       ids,
//       variables,
//       meta,
//     }: {
//       resource: string;
//       ids: string[];
//       variables?: TVariables;
//       meta?: MetaQuery;
//     }) => {
//       if (!db) throw new Error('Firestore is not initialized.');
//       await ensureAuthReady();
//       const docPathBase = meta?.collectionPath || getDefaultPath(resource);
//       const batch = writeBatch(db);
//       const deletedData: Partial<TData>[] = ids.map(
//         (id) => ({ id, ...(variables || {}) } as Partial<TData>)
//       );

//       for (const id of ids) {
//         const docRef = doc(db, docPathBase, String(id));
//         batch.delete(docRef);
//       }
//       await batch.commit();
//       return { data: convertTimestampsToISO(deletedData) as TData[] };
//     },

//     // custom: async ({ url, method, filters, sorters, payload, query, headers, meta }) => {
//     //     if (!db) throw new Error("Firestore is not initialized.");
//     //     await ensureAuthReady();
//     //     // Example: Call a Firebase Cloud Function
//     //     // if (url === 'myCustomFunction' && method === 'post') {
//     //     //   const functions = getFunctions(firebaseApp);
//     //     //   const callable = httpsCallable(functions, url);
//     //     //   const response = await callable(payload);
//     //     //   return { data: response.data as any };
//     //     // }
//     //     throw new Error(`Custom method for URL ${url} and method ${method} not implemented.`);
//     // }
//   };
// };

// // --- Example Usage (Illustrative - not part of the provider file) ---
// //
// // import { Refine } from "@refinedev/core";
// // import { firestoreProvider, FirestoreProviderOptions } from "./firestoreProvider"; // Adjust path
// //
// // const App = () => {
// //   // Configure the provider
// //   const providerOptions: FirestoreProviderOptions = {
// //     isDataPublic: true, // Default, or manage per resource via meta
// //     // Example custom path function:
// //     // getCollectionPathFn: (resource, userId, appId) => {
// //     //   if (resource === "userProfiles") return `users/${userId}/profile`; // Highly custom
// //     //   return `artifacts/${appId}/public/data/${resource}`; // Default fallback
// //     // }
// //   };
// //   const myFirestoreProvider = firestoreProvider(providerOptions);
// //
// //   // Wait for Firebase Auth to be ready before rendering Refine app
// //   // This is a common pattern to avoid issues with auth-dependent logic
// //   const [authReady, setAuthReady] = React.useState(false);
// //   React.useEffect(() => {
// //     const unsubscribe = onAuthStateChanged(auth, (user) => {
// //       setAuthReady(true);
// //       unsubscribe(); // Unsubscribe after first auth state determination
// //     });
// //     // Or, if you don't want to block rendering:
// //     // ensureAuthReady().then(() => setAuthReady(true));
// //   }, []);

// //   if (!authReady) {
// //     return <div>Loading authentication...</div>;
// //   }
// //
// //   return (
// //     <Refine
// //       dataProvider={myFirestoreProvider}
// //       // resources={[
// //       //   { name: "posts", list: "/posts", create: "/posts/create", edit: "/posts/edit/:id", show: "/posts/show/:id" },
// //       //   // Add other resources
// //       // ]}
// //       // ... other Refine props (routerProvider, notificationProvider, etc.)
// //     >
// //       {/* Your application components */}
// //     </Refine>
// //   );
// // };
